import { GranteeAccess } from "./Settings";
import { TMessage } from "../i18n/tt";
import { CustomRoleItem } from "../common/customRole";
import { makeAutoObservable } from "mobx";
import { ApiService } from "../api";
import { failure, isOk, ok, pending, Result } from "../common/common";
import { BooleanFieldModel } from "../common/commonForms";
import AlertMessageStore from "../stores/AlertMessageStore";
import UserStore from "../stores/UserStore";
import { AIClient } from "../ai/AIAssistant";
import { Language } from "../i18n/languages";
import { resolveUrnReference, toUserReference } from "../utils/UrnReference";
import { newUuid } from "../utils/uuid";
import { LocalizedTextEditorModel } from "../text/localizedTextEditorDomain";

export enum PermissionPrivilege {
  READ = "READ",
  WRITE = "WRITE",
  OWNER = "OWNER",
}

export type GetPermissionsResponse = {
  permissions: ObjectPermission[];
  admins: GranteeAccess[];
  roleItems: CustomRoleItem[];
};

export type ObjectPermission = {
  id: string;
  objectId: string;
  grantee: string;
  privilege: PermissionPrivilege;
  deleted: boolean;
};

export type ChangePermissionsRequest = {
  objectId: string;
  add: ObjectPermission[];
  change: ObjectPermission[];
  remove: ObjectPermission[];
  notifyNewPermissions: boolean;
  emailBody?: string;
};

export const privilegeLabels: { [key in PermissionPrivilege]: TMessage } = {
  [PermissionPrivilege.READ]: { key: "permission.privilege.read" },
  [PermissionPrivilege.WRITE]: { key: "permission.privilege.write" },
  [PermissionPrivilege.OWNER]: { key: "permission.privilege.write" },
};

export class ObjectPermissionModel {
  id: string;
  grantee: string;
  privilege: PermissionPrivilege;
  deleted: boolean = false;
  isExistingPermission: boolean;
  initialPermission: ObjectPermission;

  constructor(permission: ObjectPermission, isExisting: boolean = true) {
    this.initialPermission = permission;
    this.id = permission.id;
    this.grantee = permission.grantee;
    this.privilege = permission.privilege;
    this.isExistingPermission = isExisting;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isOwner() {
    return this.privilege === PermissionPrivilege.OWNER;
  }

  removePermission() {
    this.deleted = true;
  }

  restorePermission() {
    this.deleted = false;
  }

  get isNew(): boolean {
    return !this.isExistingPermission && !this.deleted;
  }

  get isRemoved(): boolean {
    return this.isExistingPermission && this.deleted;
  }

  get isChanged() {
    return (
      this.isExistingPermission &&
      !this.deleted &&
      this.privilege !== this.initialPermission.privilege
    );
  }

  toObjectPermission(): ObjectPermission {
    return {
      id: this.id,
      objectId: this.initialPermission.objectId,
      grantee: this.grantee,
      privilege: this.privilege,
      deleted: this.deleted,
    };
  }
}

export class ChangePermissionsModel {
  title: string | TMessage;
  objectId: string;
  apiService: ApiService;
  objectPermissionResult?: Result<GetPermissionsResponse, TMessage>;
  submitPermissionResult?: Result<ObjectPermission[], TMessage>;
  permissions: ObjectPermissionModel[] = [];
  notifyNewPermissions: BooleanFieldModel;
  emailBody: LocalizedTextEditorModel;

  constructor(
    title: string | TMessage,
    apiService: ApiService,
    objectId: string,
    readonly onUpdate: (permissions: ObjectPermission[]) => void,
    readonly onClose: () => void,
    readonly messageStore: AlertMessageStore,
    readonly userStore: UserStore,
    readonly aiClient: AIClient,
  ) {
    this.title = title;
    this.apiService = apiService;
    this.objectId = objectId;
    this.notifyNewPermissions = new BooleanFieldModel();
    this.emailBody = new LocalizedTextEditorModel({
      texts: [{ text: "", language: Language.NO, translationOption: { autoTranslate: false } }],
      isReadOnly: () => false,
      label: { key: "customize-message" },
      aiClient: aiClient,
    });
    makeAutoObservable(this, { apiService: false, onClose: false }, { autoBind: true });
  }

  get orgNo() {
    return this.userStore.orgNumber;
  }

  get companyName() {
    return this.userStore.companyName;
  }

  *fetchPermissions() {
    try {
      this.objectPermissionResult = pending();
      const response: GetPermissionsResponse = yield this.apiService.fetchPermissions(
        this.orgNo,
        this.objectId,
      );
      this.objectPermissionResult = ok(response);
      this.onUpdate(response.permissions);
      this.permissions = response.permissions.map((p) => new ObjectPermissionModel(p));
    } catch (e) {
      this.objectPermissionResult = failure({ key: "general-error" });
    }
  }

  get currentPermissions(): ObjectPermissionModel[] {
    return this.permissions
      .filter((d) => !d.deleted && this.findGranteeFromPermission(d.grantee))
      .sort((a, b) => Number(b.isOwner) - Number(a.isOwner));
  }

  findGranteeFromPermission(grantee: string): GranteeAccess | undefined {
    const admin = this.admins.find((admin) => grantee === toUserReference(admin.id));
    if (!admin || admin.isRevoked) {
      const viaRole = this.granteeViaRoles.find((g) => grantee === toUserReference(g.id));
      if (viaRole && !viaRole.isRevoked) {
        return viaRole;
      }
    }
    return admin;
  }

  get admins(): GranteeAccess[] {
    return isOk(this.objectPermissionResult) ? this.objectPermissionResult.data.admins : [];
  }

  get granteeViaRoles(): GranteeAccess[] {
    if (isOk(this.objectPermissionResult)) {
      const grantees = this.objectPermissionResult.data.permissions.map((p) => {
        const urn = resolveUrnReference(p.grantee);
        if (urn?.type === "user") {
          return urn.id;
        }
        return p.grantee;
      });
      return this.objectPermissionResult.data.roleItems
        .map((r) =>
          r.users
            .filter((u) => grantees.includes(u.externalId))
            .map<GranteeAccess>((u) => ({
              id: u.externalId,
              name: u.name,
              email: u.email ?? "",
              isRevoked: false,
            })),
        )
        .flatMap((u) => u);
    } else {
      return [];
    }
  }

  get isSoleAdmin() {
    return this.admins.length === 1;
  }

  get adminsWithoutPermission(): GranteeAccess[] {
    return this.admins.filter(
      (admin) =>
        !admin.isRevoked &&
        this.currentPermissions.every(
          (permission) => permission.grantee !== toUserReference(admin.id),
        ),
    );
  }

  get numberOfAdminsWithoutPermission() {
    return this.adminsWithoutPermission.length;
  }

  addAllPermissions() {
    this.adminsWithoutPermission.map((admin) => this.addPermission(admin));
  }

  addPermission(admin: GranteeAccess) {
    const permission = this.permissions.find(
      (permission) => permission.grantee === toUserReference(admin.id),
    );
    if (permission !== undefined) {
      permission.restorePermission();
    } else {
      this.permissions.push(new ObjectPermissionModel(this.mapAdminToPermission(admin), false));
    }
  }

  mapAdminToPermission(admin: GranteeAccess): ObjectPermission {
    return {
      id: newUuid(),
      objectId: this.objectId,
      grantee: toUserReference(admin.id),
      privilege: PermissionPrivilege.WRITE,
      deleted: false,
    };
  }

  *submitPermissions() {
    try {
      this.submitPermissionResult = pending();
      const result: ObjectPermission[] = yield this.apiService.changePermissions(
        this.orgNo,
        this.toChangePermissionRequest(),
      );
      this.submitPermissionResult = ok(result);
      this.onUpdate(result);
      this.messageStore.showSuccess({
        key: "permissions.submit.success",
        params: { name: this.title },
      });
      this.onClose();
    } catch (e) {
      this.submitPermissionResult = failure({ key: "general-error" });
    }
  }

  get numberOfNewPermissions() {
    return this.newPermissions.length;
  }

  get newPermissions() {
    return this.permissions.filter((permission) => permission.isNew);
  }

  get changedPermissions() {
    return this.permissions.filter((permission) => permission.isChanged);
  }

  get removedPermissions() {
    return this.permissions.filter((permission) => permission.isRemoved);
  }

  get isSubmitDisabled() {
    return (
      this.newPermissions.length === 0 &&
      this.changedPermissions.length === 0 &&
      this.removedPermissions.length === 0
    );
  }

  toChangePermissionRequest(): ChangePermissionsRequest {
    return {
      objectId: this.objectId,
      add: this.newPermissions.map((permission) => permission.toObjectPermission()),
      change: this.changedPermissions.map((permission) => permission.toObjectPermission()),
      remove: this.removedPermissions.map((permission) => permission.toObjectPermission()),
      notifyNewPermissions: this.notifyNewPermissions.value ?? false,
      emailBody: this.emailBody.textModels[0].model.isEmpty
        ? undefined
        : this.emailBody.textModels[0].model.inputValue,
    };
  }
}
